import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, isCardTransparent, getAnimation, hexToRgba } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

const TYPE_COLORS = { new: '#16a34a', fix: '#2563eb', improvement: '#9333ea', breaking: '#dc2626' };
const TYPE_LABELS = { new: 'Nuevo', fix: 'Fix', improvement: 'Mejora', breaking: 'Breaking' };
const TYPE_ICONS = {
    new: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />,
    fix: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.573-1.066z" />,
    improvement: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />,
    breaking: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />,
};

function TypeBadge({ type }) {
    const bg = TYPE_COLORS[type] || '#6b7280';
    const label = TYPE_LABELS[type] || type;
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-white uppercase tracking-wide" style={{ backgroundColor: bg }}>
            {label}
        </span>
    );
}

export default function ChangelogSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const items = content.items || [];
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const variant = config.variant || 'timeline';
    const showBadges = config.show_badges !== false;

    if (items.length === 0) return null;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    // ── Compact variant ──
    if (variant === 'compact') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="max-w-2xl mx-auto divide-y" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6' }}>
                        {items.map((item, i) => (
                            <div key={i} className={`flex items-center gap-4 py-3 transition-all duration-500 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
                                style={{ transitionDelay: visible ? `${200 + i * 80}ms` : '0ms' }}>
                                <div className="w-5 h-5 shrink-0" style={{ color: TYPE_COLORS[item.type] || '#6b7280' }}>
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        {TYPE_ICONS[item.type] || TYPE_ICONS.improvement}
                                    </svg>
                                </div>
                                <span className="text-xs shrink-0 font-mono" style={{ color: s.textSub }}>{item.date}</span>
                                {showBadges && item.version && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded shrink-0" style={{ backgroundColor: hexToRgba(s.accent, 0.1), color: s.accent }}>{item.version}</span>}
                                <span className="text-sm font-medium truncate" style={{ color: s.text }}>{item.title}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Cards variant ──
    if (variant === 'cards') {
        const columns = config.columns || 3;
        const cols = columns === 2 ? 'md:grid-cols-2' : columns === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2 lg:grid-cols-3';
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`grid grid-cols-1 ${cols} gap-6`}>
                        {items.map((item, i) => (
                            <div key={i} className={`${card} p-6 transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${200 + i * 100}ms` : '0ms' }}>
                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-xs font-medium" style={{ color: s.textSub }}>{item.date}</span>
                                    {showBadges && item.version && (
                                        <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ backgroundColor: hexToRgba(s.accent, 0.1), color: s.accent }}>{item.version}</span>
                                    )}
                                </div>
                                {showBadges && item.type && <div className="mb-3"><TypeBadge type={item.type} /></div>}
                                <h4 className="font-bold text-base mb-2" style={{ color: s.text }}>{item.title}</h4>
                                {item.description && <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>{item.description}</p>}
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Timeline variant (default) ──
    // Group items by month
    const grouped = {};
    items.forEach(item => {
        const key = item.date ? item.date.substring(0, 7) : 'Sin fecha';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(item);
    });
    const months = Object.keys(grouped);

    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                <div className="relative max-w-2xl mx-auto">
                    {/* Timeline line */}
                    <div className="absolute left-6 top-0 bottom-0 w-0.5" style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.12)' : '#e5e7eb' }} />
                    <div className="space-y-10">
                        {months.map((month, mi) => (
                            <div key={mi}>
                                {/* Month label */}
                                <div className="relative pl-16 mb-6">
                                    <div className="absolute left-3 top-1 w-6 h-6 rounded-full flex items-center justify-center z-10" style={{ backgroundColor: s.accent }}>
                                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <span className="text-sm font-bold uppercase tracking-wider" style={{ color: s.accent }}>{month}</span>
                                </div>
                                {grouped[month].map((item, i) => {
                                    const globalIdx = items.indexOf(item);
                                    return (
                                        <div key={i} className={`relative pl-16 pb-8 transition-all duration-500 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'}`}
                                            style={{ transitionDelay: visible ? `${200 + globalIdx * 120}ms` : '0ms' }}>
                                            {/* Dot */}
                                            <div className="absolute left-4 top-1.5 w-4 h-4 rounded-full border-[3px] z-10"
                                                style={{ backgroundColor: TYPE_COLORS[item.type] || s.accent, borderColor: s.dark ? 'rgba(0,0,0,0.4)' : '#ffffff' }} />
                                            <div className="flex flex-wrap items-center gap-2 mb-1.5">
                                                <span className="text-xs" style={{ color: s.textSub }}>{item.date}</span>
                                                {showBadges && item.version && (
                                                    <span className="text-[11px] font-mono px-2 py-0.5 rounded" style={{ backgroundColor: hexToRgba(s.accent, 0.1), color: s.accent }}>{item.version}</span>
                                                )}
                                                {showBadges && item.type && <TypeBadge type={item.type} />}
                                            </div>
                                            <h4 className="font-bold text-base" style={{ color: s.text }}>{item.title}</h4>
                                            {item.description && <p className="text-sm mt-1 leading-relaxed" style={{ color: s.textSub }}>{item.description}</p>}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
